// Service pour la gestion des articles
export interface Article {
  id: string;
  catalogueId?: string; // NOUVEAU: ID du catalogue pour la suppression
  code: string;
  nom: string;
  description: string;
  prix: any;
  unite: string;
  id_lot_technique?: number; // ✅ Ajouter id_lot_technique pour le formatage des prix
  quantite: number;
  totalHT: number;
  prixDegressifs?: {
    palier1: { min: number; max: number; prix: number };
    palier2: { min: number; max: number; prix: number };
    palier3: { min: number; prix: number };
  };
  detailsComplets?: {
    titre: string;
    descriptionLongue: string;
    prixParPrestataire: {
      [rang: string]: {
        palier1: string;
        palier2: string;
        palier3: string;
      };
    };
  };
}

// Service API pour les articles
export class ArticlesService {
  private static async fetchWithAuth(url: string, options: RequestInit = {}) {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Aucun token d\'authentification trouvé');
    }

    const response = await fetch(`${process.env.REACT_APP_API_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Génère le numéro de référence formaté
   * @param departement Numéro du département
   * @param idLot ID du lot
   * @param code Code de l'article
   * @param rang Rang du prestataire
   * @returns Référence formatée
   */
  private static generateReference(departement: string | number, idLot: string | number, code: string, rang: string | number): string {
    return `${departement} ${idLot} ${code} ${rang}`;
  }

  /**
   * Récupère les informations du département depuis les commandes et l'article
   * @param commandes Array des commandes
   * @param articleItem Article actuel pour récupérer son id_lot_technique
   * @returns Informations du département et lot
   */
  private static extractDepartementInfo(commandes: any[], articleItem?: any): { departement: string; idLot: string } {
    console.log('=== DEBUG extractDepartementInfo ===');
    console.log('Commandes reçues:', commandes);
    console.log('Article item reçu:', articleItem);

    if (commandes.length === 0) {
      console.log('Aucune commande fournie, utilisation des valeurs par défaut');
      return { departement: '00', idLot: '000' };
    }

    // Prendre les infos de la première commande
    const firstCommande = commandes[0];
    console.log('Première commande:', firstCommande);

    // CORRECTION: Essayer plusieurs chemins possibles pour extraire le département
    let departement = '00';
    let idLot = '000';

    // Essayer d'extraire le département
    if (firstCommande.operation?.lot_departement?.departement?.id) {
      departement = firstCommande.operation.lot_departement.departement.id.toString().padStart(2, '0');
      console.log('Département trouvé via operation.lot_departement.departement.id:', departement);
    } else if (firstCommande.lot_departement?.departement?.id) {
      departement = firstCommande.lot_departement.departement.id.toString().padStart(2, '0');
      console.log('Département trouvé via lot_departement.departement.id:', departement);
    } else if (firstCommande.operation?.id_lot_departement) {
      departement = firstCommande.operation.id_lot_departement.toString().padStart(2, '0');
      console.log('Département trouvé via operation.id_lot_departement:', departement);
    } else if (firstCommande.id_lot_departement) {
      departement = firstCommande.id_lot_departement.toString().padStart(2, '0');
      console.log('Département trouvé via id_lot_departement:', departement);
    } else {
      console.log('Département non trouvé, utilisation de la valeur par défaut:', departement);
    }

    // CORRECTION: Prioriser l'id_lot_technique de l'article, puis celui de l'opération
    if (articleItem && articleItem.id_lot_technique) {
      idLot = articleItem.id_lot_technique.toString();
      console.log('ID lot trouvé via articleItem.id_lot_technique:', idLot);
    } else if (firstCommande.operation?.id_lot_technique) {
      idLot = firstCommande.operation.id_lot_technique.toString();
      console.log('ID lot trouvé via operation.id_lot_technique:', idLot);
    } else if (firstCommande.id_lot_technique) {
      idLot = firstCommande.id_lot_technique.toString();
      console.log('ID lot trouvé via id_lot_technique:', idLot);
    } else if (firstCommande.operation?.lot_technique?.id) {
      idLot = firstCommande.operation.lot_technique.id.toString();
      console.log('ID lot trouvé via operation.lot_technique.id:', idLot);
    } else if (firstCommande.lot_technique?.id) {
      idLot = firstCommande.lot_technique.id.toString();
      console.log('ID lot trouvé via lot_technique.id:', idLot);
    } else {
      console.log('ID lot non trouvé, utilisation de la valeur par défaut:', idLot);
    }

    console.log('Résultat final - Département:', departement, 'ID Lot:', idLot);
    console.log('===================================');

    return { departement, idLot };
  }

  /**
   * Récupère les articles du catalogue BC en utilisant les id_contrat des commandes
   * @param commandes Array des commandes contenant les id_contrat
   * @returns Promise<Article[]>
   */
  static async getArticlesByCommandes(commandes: any[]): Promise<Article[]> {
    try {
      console.log('=== getArticlesByCommandes (BC) ===');
      console.log('Commandes reçues:', commandes);

      if (!commandes || commandes.length === 0) {
        console.log('Aucune commande fournie');
        return [];
      }

      // CORRECTION: Extraire les IDs de contrat en tenant compte des structures différentes
      const ids = commandes.map(cmd => {
        // Pour les prestataires transférés, le contrat peut être dans cmd directement
        if (cmd.id_contrat) {
          return cmd.id_contrat;
        }
        // Ou dans cmd.contrat.id
        if (cmd.contrat?.id) {
          return cmd.contrat.id;
        }
        // Pour les structures d'opération, essayer via l'opération
        if (cmd.operation && commandes.length === 1) {
          // Si c'est une opération transférée, utiliser les commandes de l'opération
          const operationCommandes = cmd.operation.commandes || [];
          return operationCommandes.map(opCmd => opCmd.id_contrat).filter(Boolean);
        }
        return null;
      }).flat().filter(Boolean);

      if (ids.length === 0) {
        console.log('Aucun ID de contrat trouvé dans les commandes');
        return [];
      }

      const idsString = [...new Set(ids)].join(','); // Dédupliquer
      console.log('IDs de contrats pour le catalogue:', idsString);

      const response = await this.fetchWithAuth(`/bdc/catalogue/prix?catalogue_prix_id_lot_contrat=${idsString}`);

      if (response.status !== 'success' || !response.data) {
        throw new Error('Format de réponse API inattendu');
      }

      // CORRECTION: Adapter les données de l'API à la nouvelle structure avec références
      const articles: Article[] = response.data.data.map((item: any) => {
        console.log('--- Processing article ---');
        console.log('Article item:', item);

        // CORRECTION: Extraire les informations de département et lot POUR CHAQUE ARTICLE
        const { departement, idLot } = this.extractDepartementInfo(commandes, item);
        console.log('Informations extraites pour cet article - Département:', departement, 'ID Lot:', idLot);

        // Trouver le prix correspondant au premier contrat disponible
        const prixData = item.prix && item.prix.length > 0 ? item.prix[0] : null;
        console.log('Prix data:', prixData);

        // CORRECTION: Générer la référence formatée
        const rang = prixData?.rang || '1';
        const reference = this.generateReference(departement, idLot, item.code, rang);

        console.log('Référence générée:', reference);
        console.log('Détails: dept=' + departement + ', lot=' + idLot + ', code=' + item.code + ', rang=' + rang);

        // Calculer les prix dégressifs basés sur prix_a, prix_b, prix_c
        const prixDegressifs = prixData ? {
          palier1: {
            min: 1,
            max: item.seuil_a || 5,
            prix: parseFloat(prixData.prix_a) || 0
          },
          palier2: {
            min: (item.seuil_a || 5) + 1,
            max: item.seuil_b || 10,
            prix: parseFloat(prixData.prix_b) || 0
          },
          palier3: {
            min: (item.seuil_b || 10) + 1,
            prix: parseFloat(prixData.prix_c) || 0
          }
        } : undefined;

        const article = {
          id: item.id?.toString() || '',
          code: reference, // CORRECTION: Référence formatée
          nom: item.nom || '',
          description: item.description || '',
          prix: prixData,
          unite: item.unite || 'Unité',
          id_lot_technique: item.id_lot_technique, // ✅ AJOUTER pour le formatage des prix à 4 décimales
          quantite: 0,
          totalHT: 0,
          prixDegressifs
        };

        console.log('Article final:', article);
        return article;
      });

      console.log(`Articles du catalogue BC récupérés: ${articles.length}`);
      console.log('=====================================');
      return articles;
    } catch (error) {
      console.error('Erreur lors de la récupération des articles du catalogue BC:', error);
      throw new Error('Impossible de récupérer les articles du catalogue');
    }
  }

  /**
   * Récupère les articles du catalogue BC (méthode de compatibilité)
   * @param lotIds IDs des lots de contrat
   * @returns Promise<Article[]>
   */
  static async getArticlesByLot(lotIds: string | number[]): Promise<Article[]> {
    try {
      console.warn('getArticlesByLot est obsolète pour BC, utilisez getArticlesByCommandes à la place');

      // Convertir en tableau si c'est un seul ID
      const ids = Array.isArray(lotIds) ? lotIds : [lotIds];
      const idsString = ids.join(',');

      const response = await this.fetchWithAuth(`/bdc/catalogue/prix?catalogue_prix_id_lot_contrat=${idsString}`);

      if (response.status !== 'success' || !response.data) {
        throw new Error('Format de réponse API inattendu');
      }

      // CORRECTION: Adapter les données avec références par défaut
      const articles: Article[] = response.data.data.map((item: any) => {
        // Trouver le prix correspondant au premier contrat disponible
        const prixData = item.prix && item.prix.length > 0 ? item.prix[0] : null;

        // CORRECTION: Générer référence par défaut (sans info complète de commande)
        const rang = prixData?.rang || '1';
        const reference = this.generateReference('00', item.id_lot_technique || '000', item.code, rang);

        // Calculer les prix dégressifs basés sur prix_a, prix_b, prix_c
        const prixDegressifs = prixData ? {
          palier1: {
            min: 1,
            max: item.seuil_a || 5,
            prix: parseFloat(prixData.prix_a) || 0
          },
          palier2: {
            min: (item.seuil_a || 5) + 1,
            max: item.seuil_b || 10,
            prix: parseFloat(prixData.prix_b) || 0
          },
          palier3: {
            min: (item.seuil_b || 10) + 1,
            prix: parseFloat(prixData.prix_c) || 0
          }
        } : undefined;

        return {
          id: item.id?.toString() || '',
          code: reference, // CORRECTION: Référence formatée
          nom: item.nom || '',
          description: item.description || '',
          prix: prixData ? parseFloat(prixData.prix_a) || 0 : 0,
          unite: item.unite || 'Unité',
          id_lot_technique: item.id_lot_technique, // ✅ AJOUTER pour le formatage des prix à 4 décimales
          quantite: 0,
          totalHT: 0,
          prixDegressifs
        };
      });

      return articles;
    } catch (error) {
      console.error('Erreur lors de la récupération des articles du catalogue BC:', error);
      throw new Error('Impossible de récupérer les articles du catalogue');
    }
  }

  /**
   * Récupère les détails complets d'un article
   * @param articleId ID de l'article
   * @returns Promise<Article>
   */
  // static async getArticleDetails(articleId: string): Promise<Article | null> {
  //   try {
  //     const response = await this.fetchWithAuth(`/bdc/catalogue/${articleId}`);

  //     if (response.status !== 'success' || !response.data) {
  //       return null;
  //     }

  //     const item = response.data;
  //     const prixData = item.prix && item.prix.length > 0 ? item.prix[0] : null;

  //     return {
  //       id: item.id?.toString() || articleId,
  //       code: item.code || '', // Ici on garde le code original pour les détails
  //       nom: item.nom || '',
  //       description: item.description || '',
  //       prix: prixData ? parseFloat(prixData.prix_a) || 0 : 0,
  //       unite: item.unite || 'Unité',
  //       quantite: 0,
  //       totalHT: 0,
  //       detailsComplets: {
  //         titre: item.nom || '',
  //         descriptionLongue: item.description || '',
  //         prixParPrestataire: {}
  //       }
  //     };
  //   } catch (error) {
  //     console.error('Erreur lors de la récupération des détails de l\'article:', error);
  //     return null;
  //   }
  // }

  /**
   * Calcule le prix selon la quantité et les paliers pour BC
   * @param article Article concerné
   * @param quantite Quantité commandée
   * @returns Prix unitaire selon la quantité
   */
  static getPrixByQuantity(article: Article, quantite: number): number {
    if (!article.prixDegressifs || quantite === 0) {
      return article.prix;
    }

    const { palier1, palier2, palier3 } = article.prixDegressifs;

    if (quantite >= palier1.min && quantite <= palier1.max) {
      return palier1.prix;
    } else if (quantite >= palier2.min && quantite <= palier2.max) {
      return palier2.prix;
    } else if (quantite >= palier3.min) {
      return palier3.prix;
    }

    return article.prix;
  }

  /**
   * Formate le label du palier de prix pour BC
   * @param palier Palier de prix
   * @returns Label formaté
   */
  static formatPalierLabel(palier: { min: number; max?: number }): string {
    if (palier.max && palier.max > 0) {
      return `${palier.min} à ${palier.max}`;
    }
    return `${palier.min}+`;
  }

  /**
   * Obtient le prix correspondant au rang du prestataire
   * @param item Article de l'API avec prix multiples
   * @param prestataireRang Rang du prestataire (1, 2, 3)
   * @returns Prix correspondant au rang
   */
  static getPrixByRang(item: any, prestataireRang: number): any {
    if (!item.prix || item.prix.length === 0) {
      return null;
    }

    // Chercher le prix correspondant au rang
    const prixForRang = item.prix.find((prix: any) => parseInt(prix.rang) === prestataireRang);

    // Si pas trouvé, utiliser le premier disponible
    return prixForRang || item.prix[0];
  }
}

export default ArticlesService;